import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { StoreContext } from '../contexts/StoreContext';
import { AuthContext } from '../contexts/AuthContext';
import { navItems, accountItems } from '../constants/navItems';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { db } from '../firebase';
import { COLORS } from '../constants/colors';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import { formatAddress } from '../utils/address';
import { formatBusinessHoursRows } from '../utils/businessHours';
import { groupCategories, AUTH_ACTION_LABEL } from '../constants/settings';
import { FONT_CAPTION, FONT_BODY } from '../constants/typography';
import { useSocialMedia } from '../contexts/SocialMediaContext';
import './Footer.css';

export default function Footer() {
  const { store } = useContext(StoreContext);
  const { showContact, showFAQ, showCart } = useDisplayPreferences();
  const { user } = useContext(AuthContext);
  const filteredAccountItems = (user
    ? accountItems
    : accountItems
        .filter(item => item.path !== '/bookmarks' && item.path !== '/orderhistory')
        .map(item => item.path === '/profile'
          ? { path: '/login', label: AUTH_ACTION_LABEL, icon: 'login' }
          : item
        )
  ).filter(item => item.path !== '/cart' || showCart);
  const [categories, setCategories] = useState([]);
  const businessName = store?.name?.trim();
  const currentYear = new Date().getFullYear();
  const formattedAddress = formatAddress(store?.location);
  const phoneNumber = store?.storeContact?.phoneNumber?.trim();
  const emailAddress = store?.storeContact?.email?.trim();
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';
  const smsHref = phoneNumber ? `sms:${phoneNumber.replace(/[^+\d]/g, '')}` : '';
  const hoursRows = formatBusinessHoursRows(store?.businessHours);
  const hasHours = hoursRows.length > 0;
  const hasContact = !!(phoneNumber || emailAddress);
  const { socialMedia } = useSocialMedia();

  const PLATFORMS = [
    { key: 'facebook',  label: 'Facebook',  iconClass: 'fab fa-facebook-f',  baseUrl: 'https://facebook.com/' },
    { key: 'instagram', label: 'Instagram', iconClass: 'fab fa-instagram',   baseUrl: 'https://instagram.com/' },
    { key: 'threads',   label: 'Threads',   iconClass: 'fab fa-threads',     baseUrl: 'https://threads.net/@' },
    { key: 'tiktok',    label: 'TikTok',    iconClass: 'fab fa-tiktok',      baseUrl: 'https://tiktok.com/@' },
    { key: 'youtube',   label: 'YouTube',   iconClass: 'fab fa-youtube',     baseUrl: 'https://youtube.com/@' },
    { key: 'twitter',   label: 'X',         iconClass: 'fab fa-x-twitter',   baseUrl: 'https://x.com/' },
    { key: 'linkedin',  label: 'LinkedIn',  iconClass: 'fab fa-linkedin-in', baseUrl: 'https://linkedin.com/in/' },
    { key: 'pinterest', label: 'Pinterest', iconClass: 'fab fa-pinterest-p', baseUrl: 'https://pinterest.com/' },
  ];

  const socialLinks = socialMedia
    ? PLATFORMS
        .filter(p => socialMedia[p.key]?.trim())
        .map(p => {
          const value = socialMedia[p.key].trim();
          return {
            href: value.startsWith('http') ? value : `${p.baseUrl}${value}`,
            label: p.label,
            iconClass: p.iconClass,
          };
        })
    : [];

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, 'categories'));
        if (!isMounted) return;
        const fetched = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => !!(cat?.title || cat?.name))
          .sort((a, b) => {
            const titleA = (a.title || a.name || '').toLowerCase();
            const titleB = (b.title || b.name || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });

        // Apply the same sorting logic as NavigationBar based on groupCategories setting
        const sortedCategories = groupCategories
          ? fetched // If grouping is on, use original alphabetical order
          : [...fetched].sort((a, b) => {
              // If grouping is off, sort with Product and Clearance at the bottom
              const titleA = (a.title || a.name || '').trim();
              const titleB = (b.title || b.name || '').trim();

              // Clearance always goes to the very bottom
              if (titleA === 'Clearance') return 1;
              if (titleB === 'Clearance') return -1;

              // Product goes above Clearance but below everything else
              if (titleA === 'Product') return 1;
              if (titleB === 'Product') return -1;

              // Everything else is sorted alphabetically
              return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
            });

        setCategories(sortedCategories);
      } catch (err) {
        if (isMounted) {
          setCategories([]);
        }
      }
    }

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const mapsUrl = () => {
    if (!formattedAddress) return '#';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps/search/?api=1&query=${addressString}`;
  };

  const address = formattedAddress;

  return (
    <footer
      className="footer"
      style={{
        background: COLORS.lightBackground,
        '--font-caption': FONT_CAPTION,
        '--font-body': FONT_BODY,
      }}
    >
      <div
        className="footer-inner"
        style={{
          maxWidth: MAX_CONTENT_WIDTH,
          margin: '0 auto',
          width: '100%',
          padding: '0 12px',
          boxSizing: 'border-box',
        }}
      >
        <div className="footer-columns">
          {/* Brand column */}
          <div className="footer-brand">
            <Link to="/" className="footer-brand-name">{businessName}</Link>
            {address && (
              <a href={mapsUrl()} target="_blank" rel="noopener noreferrer" className="footer-link">
                {address}
              </a>
            )}
            {phoneNumber && (
              <span className="footer-link">
                Call or text us at{' '}
                <a href={phoneHref} className="footer-link footer-link-inline">{phoneNumber}</a>
              </span>
            )}
            {emailAddress && (
              <a href={`mailto:${emailAddress}`} className="footer-link">{emailAddress}</a>
            )}
          </div>

          <div className="footer-links-group">
            {/* Quick Links column */}
            <div className="footer-section">
              <p className="footer-heading">Quick Links</p>
              <div className="footer-links">
                {navItems.filter(item =>
                  (item.path !== '/contact' || showContact) &&
                  (item.path !== '/faq' || showFAQ)
                ).map(item => (
                  <Link key={item.path} to={item.path} className="footer-link">
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Account column */}
            {filteredAccountItems.length > 0 && (
              <div className="footer-section">
                <p className="footer-heading">Account</p>
                <div className="footer-links">
                  {filteredAccountItems.map(item => (
                    <Link key={item.path} to={item.path} className="footer-link">
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Categories column */}
            {categories.length > 0 && (
              <div className="footer-section">
                <p className="footer-heading">Categories</p>
                <div className="footer-links">
                  {categories.map(category => {
                    const label = category.title || category.name || 'Category';
                    return (
                      <Link
                        key={category.id}
                        to={`/category/${category.id}`}
                        className="footer-link"
                      >
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Social column */}
            {socialLinks.length > 0 && (
              <div className="footer-section">
                <p className="footer-heading">Socials</p>
                <div className="footer-social-icons">
                  {socialLinks.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-social-link"
                      aria-label={link.label}
                    >
                      <i className={link.iconClass} aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-row">
            <p className="footer-copy">{`© ${currentYear} ${businessName}. All rights reserved.`}</p>
            <div className="footer-legal-links">
              <Link to="/terms" className="footer-copy-link">Terms of Service</Link>
              <span className="footer-legal-separator">&middot;</span>
              <Link to="/privacy" className="footer-copy-link">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
